/**
 * AlpineBlocks class for external programmatic usage
 * Provides a class-based interface for initializing and managing AlpineBlocks editors
 */
export default class AlpineBlocks {
    constructor(config = {}) {
        this.config = {
            holder: config.holder || null,
            tools: config.tools || [],
            media: config.media || null,
            ...config
        };
        this.instance = null;
        this.holder = this.config.holder;
    }

    // Static method for global configuration
    static configure(config) {
        if (!window.AlpineBlocksConfig) {
            window.AlpineBlocksConfig = {};
        }
        Object.assign(window.AlpineBlocksConfig, config);
    }

    async init() {
        if (!this.holder) {
            throw new Error('AlpineBlocks: holder element is required');
        }

        // Ensure Alpine is available
        if (!window.Alpine) {
            throw new Error('AlpineBlocks: Alpine.js is required');
        }

        // Set up the holder element with proper Alpine.js structure
        this.holder.innerHTML = `
            <div class="alpine-blocks-editor" id="${this.holder.id || 'alpine-editor'}" x-data="alpineEditor()">
                <div class="editor-area">
                    <div class="editor-content" x-ref="editorContent">
                        <template x-for="block in blocks" :key="block.id">
                            <div x-html="block.editorRender()"></div>
                        </template>
                    </div>
                </div>
            </div>
        `;

        // Configure the editor with tools
        if (this.config.tools.length > 0) {
            this.holder.querySelector('[x-data]').setAttribute('x-data', 
                `alpineEditor({ tools: ${JSON.stringify(this.config.tools)}, media: ${JSON.stringify(this.config.media)} })`
            );
        }

        // Initialize Alpine component
        await new Promise(resolve => {
            const checkEditor = () => {
                const editorId = this.holder.id || 'alpine-editor';
                if (window.alpineEditors && window.alpineEditors[editorId]) {
                    this.instance = window.alpineEditors[editorId];
                    resolve();
                } else {
                    setTimeout(checkEditor, 50);
                }
            };
            checkEditor();
        });

        return this;
    }

    save() {
        if (!this.instance) {
            throw new Error('AlpineBlocks: Editor not initialized. Call init() first.');
        }
        return this.instance.save();
    }

    render(data) {
        if (!this.instance) {
            throw new Error('AlpineBlocks: Editor not initialized. Call init() first.');
        }
        
        if (data && Array.isArray(data)) {
            // Load blocks from data
            this.instance.blocks = [];
            data.forEach(blockData => {
                const block = this.instance.initBlock(blockData.class, false);
                if (block && blockData.data) {
                    Object.assign(block.config, blockData.data);
                    block.triggerRedraw();
                }
            });
        }
        
        return this.instance.getCleanHTML();
    }

    /**
     * Load content into the editor
     * @param {Array|String|Object} blocksData - Blocks data to load
     * @returns {boolean} Success status
     */
    loadContent(blocksData) {
        if (!this.instance) {
            throw new Error('AlpineBlocks: Editor not initialized. Call init() first.');
        }
        
        // Parse blocks data if it's a string
        let blocks = blocksData;
        if (typeof blocksData === 'string') {
            try {
                blocks = JSON.parse(blocksData);
            } catch (e) {
                console.error('AlpineBlocks: Failed to parse blocks data:', e);
                return false;
            }
        }
        
        // Ensure blocks is an array
        if (!Array.isArray(blocks)) {
            blocks = blocks?.blocks || [];
        }
        
        // Clear existing blocks
        this.instance.blockManager.blocks = [];
        
        // Create blocks from saved data
        if (blocks && blocks.length > 0) {
            blocks.forEach(blockData => {
                const blockClass = blockData.class || blockData.type || 'WYSIWYG';  // Default to WYSIWYG

                // Only create block if tool exists
                if (blockClass && this.instance.toolConfig[blockClass]) {
                    // Create block WITHOUT initializing
                    const block = this.instance.createBlockInstance(blockClass, blockData.id);

                    // Apply saved configuration BEFORE init
                    if (block && blockData.data) {
                        // Convert template metadata to pre-insert format for WYSIWYG init
                        if (blockData.data.templateId && blockData.data.templateProperties) {
                            blockData.data._templateIdToInsert = blockData.data.templateId;
                            blockData.data._templatePropertiesToInsert = blockData.data.templateProperties;
                        }
                        Object.assign(block.config, blockData.data);
                    }

                    // NOW initialize with complete config
                    if (block) {
                        block.init(this.instance);
                        this.instance.blocks.push(block);
                    }
                }
            });
        }
        
        // Add default paragraph if no blocks loaded
        if (this.instance.blockManager.blocks.length === 0 && this.instance.toolConfig['Paragraph']) {
            this.instance.initBlock('Paragraph', true);
        }
        
        // Dispatch events
        document.dispatchEvent(new CustomEvent('editor-clear-selection'));
        
        setTimeout(() => {
            document.dispatchEvent(new CustomEvent('editor-changed'));
        }, 100);
        
        return true;
    }

    getHTML() {
        if (!this.instance) {
            throw new Error('AlpineBlocks: Editor not initialized. Call init() first.');
        }
        return this.instance.getCleanHTML();
    }

    destroy() {
        if (this.instance && this.holder) {
            // Clean up
            const editorId = this.holder.id || 'alpine-editor';
            if (window.alpineEditors && window.alpineEditors[editorId]) {
                delete window.alpineEditors[editorId];
            }
            this.holder.innerHTML = '';
            this.instance = null;
        }
    }
}